/**
 * Customer 模块 API 工厂函数
 * 
 * @description
 * 本文件提供 Customer 模块的 API 工厂函数，通过依赖注入的方式支持多端（Web/App）使用。
 * 
 * @module API/Customer/Factory
 * @backend backend/app/Modules/Sales/Controllers/CustomerController.php
 * @routes backend/routes/api.php - /api/v1/sales/customers
 * 
 * @usage
 * ```typescript
 * // Web 端使用
 * import { createCustomerApi } from '@bayes-mes/shared/api/customer/factory';
 * import { requestClient } from '#/api/request';
 * 
 * const customerApi = createCustomerApi(requestClient);
 * const customers = await customerApi.getList({ page: 1 });
 * 
 * // App 端使用
 * import { createCustomerApi } from '@bayes-mes/shared/api/customer/factory';
 * import { http } from '@/utils/http';
 * 
 * const customerApi = createCustomerApi(http);
 * const customers = await customerApi.getList({ page: 1 });
 * ```
 * 
 * @author YSFL Team
 * @last-updated 2025-10-19
 */

// ==================== 类型导入 ====================
import type {
  // 响应类型
  Customer,
  CustomerSalesRankingItem,
  CustomerSalesTrendItem,
  
  // 请求类型
  CreateCustomerForm,
  UpdateCustomerForm,
  BatchDeleteCustomerForm,
  UpdateCustomerStatusForm,
  UpdateCreditLevelForm,
  
  // 查询参数
  CustomerQueryParams,
  CustomerSalesRankingParams,
  CustomerSalesTrendParams,
  
  // 统计类型
  CustomerGlobalStatistics,
  CustomerStatistics,
} from '../../types/customer';

import type { PaginationApiResponse, ApiResponse } from '../../types/api';
import { API_ROUTES } from '../routes';

// ==================== 类型定义 ====================

/**
 * HTTP 请求适配器接口
 * 
 * @description
 * 定义统一的 HTTP 请求接口，不同平台需要实现此接口。
 * 
 * @example Web 端适配器
 * ```typescript
 * // Web端适配器（基于axios）
 * const webAdapter: HttpAdapter = {
 *   get: (url, config) => axios.get(url, config),
 *   post: (url, data, config) => axios.post(url, data, config),
 *   put: (url, data, config) => axios.put(url, data, config),
 *   delete: (url, config) => axios.delete(url, config),
 * };
 * ```
 * 
 * @example App 端适配器
 * ```typescript
 * // App端适配器（基于uni.request）
 * const appAdapter: HttpAdapter = {
 *   get: (url, config) => http.get(url, config?.params),
 *   post: (url, data) => http.post(url, data),
 *   put: (url, data) => http.put(url, data),
 *   delete: (url) => http.delete(url),
 * };
 * ```
 */
export interface HttpAdapter {
  /**
   * GET 请求
   * @param url 请求URL
   * @param config 请求配置（包含 params 等）
   */
  get<T = any>(url: string, config?: any): Promise<T>;
  
  /**
   * POST 请求
   * @param url 请求URL
   * @param data 请求数据
   * @param config 请求配置
   */
  post<T = any>(url: string, data?: any, config?: any): Promise<T>;
  
  /**
   * PUT 请求
   * @param url 请求URL
   * @param data 请求数据
   * @param config 请求配置
   */
  put<T = any>(url: string, data?: any, config?: any): Promise<T>;
  
  /**
   * DELETE 请求
   * @param url 请求URL
   * @param config 请求配置
   */
  delete<T = any>(url: string, config?: any): Promise<T>;
}

/**
 * Customer API 接口定义
 * 
 * @description
 * 定义 Customer 模块的所有 API 方法。
 */
export interface CustomerApi {
  // ========== 基础 CRUD ==========
  
  /**
   * 获取客户列表（分页）
   * @param params 查询参数
   * @returns 客户分页列表
   * @permission sales:customer:view
   */
  getList(params?: CustomerQueryParams): Promise<PaginationApiResponse<Customer>>;
  
  /**
   * 获取客户详情
   * @param id 客户ID
   * @returns 客户详细信息
   * @permission sales:customer:view
   */
  getDetail(id: number): Promise<ApiResponse<Customer>>;
  
  /**
   * 创建客户
   * @param data 客户创建表单数据
   * @returns 创建的客户信息
   * @permission sales:customer:create
   */
  create(data: CreateCustomerForm): Promise<ApiResponse<Customer>>;
  
  /**
   * 更新客户信息
   * @param id 客户ID
   * @param data 客户更新表单数据
   * @returns 更新后的客户信息
   * @permission sales:customer:update
   */
  update(id: number, data: UpdateCustomerForm): Promise<ApiResponse<Customer>>;
  
  /**
   * 删除客户
   * @param id 客户ID
   * @returns 删除结果
   * @permission sales:customer:delete
   */
  delete(id: number): Promise<ApiResponse<null>>;
  
  // ========== 批量操作 ==========
  
  /**
   * 批量删除客户
   * @param data 批量删除表单数据
   * @returns 删除结果（包含删除数量）
   * @permission sales:customer:delete
   */
  batchDelete(data: BatchDeleteCustomerForm): Promise<ApiResponse<{ deletedCount: number; totalCount: number }>>;
  
  // ========== 查询功能 ==========
  
  /**
   * 按客户编码查询客户
   * @param code 客户编码
   * @returns 客户信息
   * @permission sales:customer:view
   */
  getByCode(code: string): Promise<ApiResponse<Customer>>;
  
  /**
   * 获取正常客户列表
   * @returns 正常客户列表
   * @permission sales:customer:view
   * @note 需要后端添加路由支持
   */
  getActive(): Promise<ApiResponse<Customer[]>>;
  
  /**
   * 获取黑名单客户列表
   * @returns 黑名单客户列表
   * @permission sales:customer:view
   * @note 需要后端添加路由支持
   */
  getBlacklisted(): Promise<ApiResponse<Customer[]>>;
  
  // ========== 统计功能 ==========
  
  /**
   * 获取客户全局统计
   * @returns 全局统计数据
   * @permission sales:customer:view
   */
  getStatistics(): Promise<ApiResponse<CustomerGlobalStatistics>>;
  
  /**
   * 获取单个客户的统计信息
   * @param id 客户ID
   * @returns 客户统计数据
   * @permission sales:customer:view
   */
  getStatisticsById(id: number): Promise<ApiResponse<CustomerStatistics>>;
  
  // ========== 状态管理 ==========
  
  /**
   * 更新客户状态
   * @param id 客户ID
   * @param data 状态更新表单数据
   * @returns 更新后的客户信息
   * @permission sales:customer:update
   * @note 需要后端添加路由支持
   */
  updateStatus(id: number, data: UpdateCustomerStatusForm): Promise<ApiResponse<Customer>>;
  
  /**
   * 更新客户信用等级
   * @param id 客户ID
   * @param data 信用等级更新表单数据
   * @returns 更新后的客户信息
   * @permission sales:customer:update
   * @note 需要后端添加路由支持
   */
  updateCreditLevel(id: number, data: UpdateCreditLevelForm): Promise<ApiResponse<Customer>>;
  
  // ========== 销量分析功能 ==========
  
  /**
   * 获取客户销量排名
   * @param params 查询参数
   * @returns 客户销量排名列表
   * @permission sales:customer:view
   */
  getSalesRanking(params?: CustomerSalesRankingParams): Promise<ApiResponse<CustomerSalesRankingItem[]>>;
  
  /**
   * 获取客户销售趋势
   * @param id 客户ID
   * @param params 查询参数
   * @returns 销售趋势数据
   * @permission sales:customer:view
   */
  getSalesTrend(id: number, params?: CustomerSalesTrendParams): Promise<ApiResponse<CustomerSalesTrendItem[]>>;
}

// ==================== API 路径兼容别名 ====================

/**
 * @deprecated 新代码请直接使用 `API_ROUTES.salesCustomers`。
 * 保留旧键名仅用于兼容已有的直接导入方，路径权威定义位于 api/routes/v1.ts。
 */
export const CUSTOMER_API_PATHS = {
  BASE: API_ROUTES.salesCustomers.list,
  LIST: API_ROUTES.salesCustomers.list,
  STATISTICS: API_ROUTES.salesCustomers.statistics,
  CREATE: API_ROUTES.salesCustomers.customers,
  DETAIL: API_ROUTES.salesCustomers.detail(':id'),
  CUSTOMER_STATISTICS: API_ROUTES.salesCustomers.customersStatistics(':id'),
  UPDATE: API_ROUTES.salesCustomers.salesCustomers(':id'),
  DELETE: API_ROUTES.salesCustomers.delete(':id'),
  BATCH_DELETE: API_ROUTES.salesCustomers.batchDelete,
  FIND_BY_CODE: API_ROUTES.salesCustomers.findByCode(':code'),
  UPDATE_STATUS: API_ROUTES.salesCustomers.update(':id'),
  UPDATE_CREDIT_LEVEL: API_ROUTES.salesCustomers.customersCreditLevel(':id'),
  ACTIVE: API_ROUTES.salesCustomers.active,
  BLACKLISTED: API_ROUTES.salesCustomers.blacklisted,
  SALES_RANKING: API_ROUTES.salesCustomers.customersSalesRanking,
  SALES_TREND: API_ROUTES.salesCustomers.customersSalesTrend(':id'),
} as const;

// ==================== 工厂函数 ====================

/**
 * 创建 Customer API 实例
 * 
 * @description
 * 通过依赖注入的方式创建 API 实例，支持不同平台的 HTTP 客户端。
 * 
 * @param http HTTP 请求适配器
 * @returns Customer API 实例
 * 
 * @example Web 端使用
 * ```typescript
 * import { createCustomerApi } from '@bayes-mes/shared/api/customer/factory';
 * import { requestClient } from '#/api/request';
 * 
 * // 创建 API 实例
 * const customerApi = createCustomerApi(requestClient);
 * 
 * // 使用 API
 * const result = await customerApi.getList({ page: 1, per_page: 20 });
 * ```
 * 
 * @example App 端使用
 * ```typescript
 * import { createCustomerApi } from '@bayes-mes/shared/api/customer/factory';
 * import { http } from '@/utils/http';
 * 
 * // 创建 API 实例
 * const customerApi = createCustomerApi(http);
 * 
 * // 使用 API
 * const result = await customerApi.getList({ page: 1, per_page: 20 });
 * ```
 */
export function createCustomerApi(http: HttpAdapter): CustomerApi {
  return {
    // ==================== 基础 CRUD ====================
    
    /**
     * 获取客户列表（分页）
     * 
     * @description
     * 支持多条件筛选和分页查询。
     * 
     * @param params 查询参数
     * @param params.page 页码（默认：1）
     * @param params.per_page 每页数量（默认：20）
     * @param params.customer_code 客户编码（模糊搜索）
     * @param params.customer_name 客户名称（模糊搜索）
     * @param params.customer_type 客户类型（1:直接客户, 2:代理商, 3:OEM）
     * @param params.credit_level 信用等级（1:优, 2:良, 3:中, 4:差, 5:黑名单）
     * @param params.status 状态（0:停用, 1:正常）
     * @param params.is_designated_supplier 是否指定供应商（0:否, 1:是）
     * @param params.province 省份
     * @param params.city 城市
     * @param params.keyword 关键词（全局搜索）
     * @param params.sort_by 排序字段
     * @param params.sort_order 排序方向（asc/desc）
     * 
     * @returns Promise<PaginationApiResponse<Customer>>
     * 
     * @permission sales:customer:view
     * @backend CustomerController::index()
     * 
     * @example
     * ```typescript
     * // 基本查询
     * const result = await customerApi.getList({ page: 1 });
     * 
     * // 按客户类型筛选
     * const result = await customerApi.getList({
     *   customer_type: CustomerType.DIRECT,
     *   page: 1,
     *   per_page: 20
     * });
     * 
     * // 按信用等级筛选
     * const result = await customerApi.getList({
     *   credit_level: CreditLevel.GOOD,
     * });
     * 
     * // 关键词搜索
     * const result = await customerApi.getList({
     *   keyword: '深圳',
     * });
     * ```
     */
    getList(params?: CustomerQueryParams): Promise<PaginationApiResponse<Customer>> {
      // 发送 GET 请求到列表端点
      // 查询参数通过 config.params 传递
      return http.get(API_ROUTES.salesCustomers.list, { params });
    },
    
    /**
     * 获取客户详情
     * 
     * @description
     * 根据客户ID获取完整的客户信息，包括关联数据（创建人、更新人）。
     * 
     * @param id 客户ID
     * 
     * @returns Promise<ApiResponse<Customer>>
     * 
     * @permission sales:customer:view
     * @backend CustomerController::show()
     * 
     * @example
     * ```typescript
     * const result = await customerApi.getDetail(1);
     * console.log(result.data.customerName);
     * console.log(result.data.creditLevelText);
     * ```
     */
    getDetail(id: number): Promise<ApiResponse<Customer>> {
      return http.get(API_ROUTES.salesCustomers.detail(id));
    },
    
    /**
     * 创建客户
     * 
     * @description
     * 创建新客户，支持完整的客户信息录入。
     * 
     * @param data 客户创建表单数据
     * @param data.customer_code 客户编码（必填，唯一）
     * @param data.customer_name 客户名称（必填）
     * @param data.customer_type 客户类型（必填：1:直接客户, 2:代理商, 3:OEM）
     * @param data.credit_level 信用等级（必填：1:优, 2:良, 3:中, 4:差, 5:黑名单）
     * @param data.short_name 客户简称（可选）
     * @param data.contact_person 联系人（可选）
     * @param data.contact_phone 联系电话（可选）
     * @param data.contact_email 联系邮箱（可选）
     * @param data.contact_mobile 联系手机（可选）
     * @param data.province 省份（可选）
     * @param data.city 城市（可选）
     * @param data.district 区县（可选）
     * @param data.address 详细地址（可选）
     * @param data.postal_code 邮政编码（可选）
     * @param data.payment_terms 付款条件（可选）
     * @param data.credit_limit 信用额度（可选）
     * @param data.industry 所属行业（可选）
     * @param data.company_size 公司规模（可选）
     * @param data.tax_number 税号（可选）
     * @param data.bank_name 开户银行（可选）
     * @param data.bank_account 银行账号（可选）
     * @param data.is_designated_supplier 是否指定供应商（可选）
     * @param data.price_agreements 价格协议（可选）
     * @param data.remark 备注（可选）
     * @param data.status 状态（可选：0:停用, 1:正常）
     * @param data.is_active 是否激活（可选）
     * 
     * @returns Promise<ApiResponse<Customer>>
     * 
     * @permission sales:customer:create
     * @backend CustomerController::store()
     * 
     * @example
     * ```typescript
     * const result = await customerApi.create({
     *   customer_code: 'CU202501010001',
     *   customer_name: '深圳市XX科技有限公司',
     *   short_name: 'XX科技',
     *   customer_type: CustomerType.DIRECT,
     *   credit_level: CreditLevel.GOOD,
     *   contact_person: '张三',
     *   contact_mobile: '13800138000',
     *   province: '广东省',
     *   city: '深圳市',
     *   district: '南山区',
     *   address: 'XX路XX号',
     *   credit_limit: 1000000.00,
     * });
     * ```
     */
    create(data: CreateCustomerForm): Promise<ApiResponse<Customer>> {
      // 发送 POST 请求到创建端点
      // 表单数据作为请求体
      return http.post(API_ROUTES.salesCustomers.customers, data);
    },
    
    /**
     * 更新客户信息
     * 
     * @description
     * 更新客户信息，支持部分字段更新。
     * 
     * @param id 客户ID
     * @param data 客户更新表单数据（所有字段可选）
     * 
     * @returns Promise<ApiResponse<Customer>>
     * 
     * @permission sales:customer:update
     * @backend CustomerController::update()
     * 
     * @example
     * ```typescript
     * // 更新客户名称
     * const result = await customerApi.update(1, {
     *   customer_name: '新客户名称',
     * });
     * 
     * // 更新信用等级和额度
     * const result = await customerApi.update(1, {
     *   credit_level: CreditLevel.EXCELLENT,
     *   credit_limit: 2000000.00,
     * });
     * ```
     */
    update(id: number, data: UpdateCustomerForm): Promise<ApiResponse<Customer>> {
      return http.put(API_ROUTES.salesCustomers.salesCustomers(id), data);
    },
    
    /**
     * 删除客户
     * 
     * @description
     * 删除指定客户（软删除）。如果客户有关联订单，将无法删除。
     * 
     * @param id 客户ID
     * 
     * @returns Promise<ApiResponse<null>>
     * 
     * @permission sales:customer:delete
     * @backend CustomerController::destroy()
     * 
     * @throws 如果客户已有关联订单，将抛出错误
     * 
     * @example
     * ```typescript
     * try {
     *   await customerApi.delete(1);
     *   console.log('删除成功');
     * } catch (error) {
     *   console.error('删除失败:', error.message);
     * }
     * ```
     */
    delete(id: number): Promise<ApiResponse<null>> {
      return http.delete(API_ROUTES.salesCustomers.delete(id));
    },
    
    // ==================== 批量操作 ====================
    
    /**
     * 批量删除客户
     * 
     * @description
     * 批量删除多个客户。会跳过有关联订单的客户，返回实际删除数量。
     * 
     * @param data 批量删除表单数据
     * @param data.ids 客户ID数组
     * 
     * @returns Promise<ApiResponse<{ deletedCount: number; totalCount: number }>>
     * 
     * @permission sales:customer:delete
     * @backend CustomerController::batchDelete()
     * 
     * @example
     * ```typescript
     * const result = await customerApi.batchDelete({
     *   ids: [1, 2, 3, 4, 5]
     * });
     * 
     * console.log(`成功删除 ${result.data.deletedCount} 个客户`);
     * console.log(`共选择 ${result.data.totalCount} 个客户`);
     * ```
     */
    batchDelete(data: BatchDeleteCustomerForm): Promise<ApiResponse<{ deletedCount: number; totalCount: number }>> {
      // 发送 POST 请求到批量删除端点
      return http.post(API_ROUTES.salesCustomers.batchDelete, data);
    },
    
    // ==================== 查询功能 ====================
    
    /**
     * 按客户编码查询客户
     * 
     * @description
     * 根据客户编码精确查询客户信息。
     * 
     * @param code 客户编码
     * 
     * @returns Promise<ApiResponse<Customer>>
     * 
     * @permission sales:customer:view
     * @backend CustomerController::findByCode()
     * 
     * @example
     * ```typescript
     * const result = await customerApi.getByCode('CU202501010001');
     * ```
     */
    getByCode(code: string): Promise<ApiResponse<Customer>> {
      return http.get(API_ROUTES.salesCustomers.findByCode(code));
    },
    
    /**
     * 获取正常客户列表
     * 
     * @description
     * 获取所有状态为正常的客户列表（不分页）。
     * 
     * @returns Promise<ApiResponse<Customer[]>>
     * 
     * @permission sales:customer:view
     * @backend CustomerController::getActive()
     * 
     * @example
     * ```typescript
     * const result = await customerApi.getActive();
     * const activeCustomers = result.data;
     * ```
     */
    getActive(): Promise<ApiResponse<Customer[]>> {
      // 发送 GET 请求到正常客户端点
      return http.get(API_ROUTES.salesCustomers.active);
    },
    
    /**
     * 获取黑名单客户列表
     * 
     * @description
     * 获取所有信用等级为黑名单的客户列表（不分页）。
     * 
     * @returns Promise<ApiResponse<Customer[]>>
     * 
     * @permission sales:customer:view
     * @backend CustomerController::getBlacklisted()
     * 
     * @example
     * ```typescript
     * const result = await customerApi.getBlacklisted();
     * const blacklistedCustomers = result.data;
     * ```
     */
    getBlacklisted(): Promise<ApiResponse<Customer[]>> {
      // 发送 GET 请求到黑名单客户端点
      return http.get(API_ROUTES.salesCustomers.blacklisted);
    },
    
    // ==================== 统计功能 ====================
    
    /**
     * 获取客户全局统计
     * 
     * @description
     * 获取客户的全局统计信息，包括：
     * - 总客户数、正常客户数、停用客户数
     * - 按客户类型分组统计
     * - 按信用等级分组统计
     * - 总信用额度、平均信用额度
     * - 有订单客户数、无订单客户数
     * 
     * @returns Promise<ApiResponse<CustomerGlobalStatistics>>
     * 
     * @permission sales:customer:view
     * @backend CustomerController::statistics()
     * 
     * @example
     * ```typescript
     * const result = await customerApi.getStatistics();
     * const stats = result.data;
     * 
     * console.log('总客户数:', stats.totalCustomers);
     * console.log('正常客户:', stats.activeCount);
     * console.log('按类型统计:', stats.byCustomerType);
     * console.log('按信用统计:', stats.byCreditLevel);
     * ```
     */
    getStatistics(): Promise<ApiResponse<CustomerGlobalStatistics>> {
      // 发送 GET 请求到全局统计端点
      return http.get(API_ROUTES.salesCustomers.statistics);
    },
    
    /**
     * 获取单个客户的统计信息
     * 
     * @description
     * 获取指定客户的统计信息，包括：
     * - 客户基本信息
     * - 订单数量、订单总金额
     * - 信用额度、已使用信用额度
     * 
     * @param id 客户ID
     * 
     * @returns Promise<ApiResponse<CustomerStatistics>>
     * 
     * @permission sales:customer:view
     * @backend CustomerController::customerStatistics()
     * 
     * @example
     * ```typescript
     * const result = await customerApi.getStatisticsById(1);
     * const stats = result.data;
     * 
     * console.log('客户编码:', stats.customerCode);
     * console.log('订单数量:', stats.orderCount);
     * console.log('订单总额:', stats.totalAmount);
     * console.log('信用额度:', stats.creditLimit);
     * console.log('已用额度:', stats.creditUsed);
     * ```
     */
    getStatisticsById(id: number): Promise<ApiResponse<CustomerStatistics>> {
      return http.get(API_ROUTES.salesCustomers.customersStatistics(id));
    },
    
    // ==================== 状态管理 ====================
    
    /**
     * 更新客户状态
     * 
     * @description
     * 更新客户的启用/停用状态。
     * 
     * @param id 客户ID
     * @param data 状态更新表单数据
     * @param data.status 状态（0:停用, 1:正常）
     * 
     * @returns Promise<ApiResponse<Customer>>
     * 
     * @permission sales:customer:update
     * @backend CustomerController::updateStatus()
     * 
     * @example
     * ```typescript
     * // 停用客户
     * const result = await customerApi.updateStatus(1, {
     *   status: CustomerStatus.INACTIVE
     * });
     * 
     * // 启用客户
     * const result = await customerApi.updateStatus(1, {
     *   status: CustomerStatus.ACTIVE
     * });
     * ```
     */
    updateStatus(id: number, data: UpdateCustomerStatusForm): Promise<ApiResponse<Customer>> {
      return http.put(API_ROUTES.salesCustomers.update(id), data);
    },
    
    /**
     * 更新客户信用等级
     * 
     * @description
     * 更新客户的信用等级。
     * 
     * @param id 客户ID
     * @param data 信用等级更新表单数据
     * @param data.credit_level 信用等级（1:优, 2:良, 3:中, 4:差, 5:黑名单）
     * 
     * @returns Promise<ApiResponse<Customer>>
     * 
     * @permission sales:customer:update
     * @backend CustomerController::updateCreditLevel()
     * 
     * @example
     * ```typescript
     * // 提升信用等级
     * const result = await customerApi.updateCreditLevel(1, {
     *   credit_level: CreditLevel.EXCELLENT
     * });
     * 
     * // 降低信用等级
     * const result = await customerApi.updateCreditLevel(1, {
     *   credit_level: CreditLevel.POOR
     * });
     * 
     * // 拉入黑名单
     * const result = await customerApi.updateCreditLevel(1, {
     *   credit_level: CreditLevel.BLACKLIST
     * });
     * ```
     */
    updateCreditLevel(id: number, data: UpdateCreditLevelForm): Promise<ApiResponse<Customer>> {
      return http.put(API_ROUTES.salesCustomers.customersCreditLevel(id), data);
    },
    
    // ==================== 销量分析功能 ====================
    
    /**
     * 获取客户销量排名
     * 
     * @description
     * 获取客户按销售金额/订单数/销售数量排名的列表。
     * 
     * @param params 查询参数
     * @param params.start_date 开始日期
     * @param params.end_date 结束日期
     * @param params.customer_type 客户类型筛选
     * @param params.sort_by 排序字段 (total_amount/order_count/total_quantity)
     * @param params.sort_order 排序方向 (asc/desc)
     * @param params.limit 返回数量
     * 
     * @returns Promise<ApiResponse<CustomerSalesRankingItem[]>>
     * 
     * @permission sales:customer:view
     * @backend CustomerController::salesRanking()
     * 
     * @example
     * ```typescript
     * // 获取本月销量排名前20
     * const result = await customerApi.getSalesRanking({
     *   start_date: '2025-01-01',
     *   end_date: '2025-01-31',
     *   sort_by: 'total_amount',
     *   limit: 20
     * });
     * ```
     */
    getSalesRanking(params?: CustomerSalesRankingParams): Promise<ApiResponse<CustomerSalesRankingItem[]>> {
      return http.get(API_ROUTES.salesCustomers.customersSalesRanking, { params });
    },
    
    /**
     * 获取客户销售趋势
     * 
     * @description
     * 获取指定客户的销售趋势数据，支持按月、季度、年统计。
     * 
     * @param id 客户ID
     * @param params 查询参数
     * @param params.period 周期类型 (month/quarter/year)
     * @param params.count 返回周期数
     * 
     * @returns Promise<ApiResponse<CustomerSalesTrendItem[]>>
     * 
     * @permission sales:customer:view
     * @backend CustomerController::salesTrend()
     * 
     * @example
     * ```typescript
     * // 获取最近12个月的销售趋势
     * const result = await customerApi.getSalesTrend(1, {
     *   period: 'month',
     *   count: 12
     * });
     * ```
     */
    getSalesTrend(id: number, params?: CustomerSalesTrendParams): Promise<ApiResponse<CustomerSalesTrendItem[]>> {
      return http.get(API_ROUTES.salesCustomers.customersSalesTrend(id), { params });
    },
  };
}

// ==================== 默认导出 ====================

/**
 * 默认导出
 * 
 * @description
 * 导出工厂函数和路径常量，方便使用。
 */
export default {
  /** 工厂函数 */
  createCustomerApi,
  /** API 路径常量 */
  PATHS: CUSTOMER_API_PATHS,
};
